import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { Filter } from "@/shared/lib/filter/filter";
import { Lst } from "@/shared/lib/lst/lst";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { ContractBaseData, ContractBaseDataPatch } from "../types/base-data";

/**
 * A save refetches the entry either way: on error to restore the server
 * state over the optimistic value, on success to pick up what the server
 * derived from it. See the create-auto-saving-input convention.
 */
const invalidatesContractBaseData = (
  _result: unknown,
  _error: unknown,
  request: { id: UUID },
) =>
  [
    { type: "OmniContractBaseData" as const, id: request.id },
    { type: "OmniContractBaseData" as const, id: "LIST" },
  ] as const;

export const omniContractBaseDataApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["OmniContractBaseData", "DefaultBaseData"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      omniContractBaseDataCreate: build.mutation<
        ContractBaseData,
        CreateRequest
      >({
        query: (request) => ({
          url: "/omni/contract/base-data",
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "OmniContractBaseData", id: "LIST" }],
      }),
      omniContractBaseDataShow: build.query<ContractBaseData, ShowRequest>({
        query: (request) => ({
          url: "/omni/contract/base-data",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("OmniContractBaseData"),
      }),
      omniContractBaseDataList: build.query<Lst<ContractBaseData>, ListRequest>(
        {
          query: (request) => ({
            url: "/omni/contract/base-data/list",
            method: "GET",
            params: base64ListRequest(request),
          }),
          providesTags: (result) =>
            providesList(result?.items, "OmniContractBaseData"),
        },
      ),
      omniContractBaseDataPatch: build.mutation<ContractBaseData, PatchRequest>(
        {
          query: (request) => ({
            url: "/omni/contract/base-data",
            method: "PATCH",
            body: request,
          }),
          invalidatesTags: invalidatesContractBaseData,
        },
      ),
      omniContractBaseDataDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/omni/contract/base-data",
          method: "DELETE",
          body: request,
        }),
        // The backend refuses the delete while the instance is a
        // default, so the defaults are refetched either way.
        invalidatesTags: [
          { type: "OmniContractBaseData", id: "LIST" },
          "DefaultBaseData",
        ],
      }),
    }),
  });

export const {
  useOmniContractBaseDataCreateMutation,
  useOmniContractBaseDataShowQuery,
  useOmniContractBaseDataListQuery,
  useOmniContractBaseDataPatchMutation,
  useOmniContractBaseDataDeleteMutation,
} = omniContractBaseDataApi;

export interface CreateRequest {
  id: UUID;
  companyId: UUID;
  serviceKind: string;
  serviceVariation: ServiceVariation;
  label: string;
}

export interface ShowRequest {
  id: UUID;
}

export interface ListRequest {
  /** Required by the backend: active, with at least one company. */
  company: Filter<UUID>;
  serviceKind: Filter<string>;
  serviceVariation?: Filter<string>;
  label?: Filter<string>;
  orderBy?: OrdrBy<"id" | "label" | "createdAt" | "updatedAt">[];
  limit?: number;
  offset?: number;
}

export type PatchRequest = ContractBaseDataPatch & { id: UUID };

export interface DeleteRequest {
  id: UUID;
}

export const updateOmniContractBaseDataCache = updateCache(
  omniContractBaseDataApi,
  "omniContractBaseDataShow",
);
